// Clé de chiffrement locale : une clé aléatoire (AES-256) chiffre les données ;
// elle n'est jamais stockée en clair, seulement enveloppée par une clé dérivée
// du code de l'utilisateur (PBKDF2) et, si l'appareil le permet, par l'extension
// PRF d'une passkey. Toutes les opérations Web Crypto ont lieu hors transaction.

export type Enveloppe = {
  sel: Uint8Array
  cle: Uint8Array // nonce (12 octets) ‖ clé de données chiffrée (AES-GCM)
}

export type EnveloppeCode = Enveloppe & { iterations: number }
export type EnveloppePasskey = Enveloppe & { credentialId: string }

// Recommandation OWASP (2023) pour PBKDF2-HMAC-SHA256
export const ITERATIONS_PBKDF2 = 600_000
export const LONGUEUR_CODE_MIN = 6

export class ErreurCode extends Error {
  constructor() {
    super('Code incorrect')
    this.name = 'ErreurCode'
  }
}

const aleatoire = (n: number) => crypto.getRandomValues(new Uint8Array(n))

/** Nouvelle clé de données (AES-256). */
export const nouvelleCle = () => aleatoire(32)

export function codeValide(code: string): boolean {
  return new RegExp(`^\\d{${LONGUEUR_CODE_MIN},12}$`).test(code)
}

async function cleDepuisCode(code: string, sel: Uint8Array, iterations: number): Promise<CryptoKey> {
  const materiau = await crypto.subtle.importKey('raw', new TextEncoder().encode(code), 'PBKDF2', false, ['deriveKey'])
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', hash: 'SHA-256', salt: sel as BufferSource, iterations },
    materiau,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  )
}

async function cleDepuisPrf(sortie: BufferSource, sel: Uint8Array): Promise<CryptoKey> {
  const materiau = await crypto.subtle.importKey('raw', sortie, 'HKDF', false, ['deriveKey'])
  return crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: sel as BufferSource, info: new TextEncoder().encode('filaplomb-coffre') },
    materiau,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  )
}

async function envelopper(donnees: Uint8Array, cle: CryptoKey): Promise<Uint8Array> {
  const nonce = aleatoire(12)
  const chiffre = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv: nonce }, cle, donnees as BufferSource))
  const r = new Uint8Array(12 + chiffre.length)
  r.set(nonce)
  r.set(chiffre, 12)
  return r
}

async function desenvelopper(enveloppe: Uint8Array, cle: CryptoKey): Promise<Uint8Array> {
  try {
    const clair = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: enveloppe.subarray(0, 12) as BufferSource },
      cle,
      enveloppe.subarray(12) as BufferSource,
    )
    return new Uint8Array(clair)
  } catch {
    throw new ErreurCode()
  }
}

export async function envelopperParCode(cleDonnees: Uint8Array, code: string, iterations = ITERATIONS_PBKDF2): Promise<EnveloppeCode> {
  const sel = aleatoire(16)
  return { sel, iterations, cle: await envelopper(cleDonnees, await cleDepuisCode(code, sel, iterations)) }
}

/** Lève ErreurCode si le code est faux (le tag AES-GCM ne correspond pas). */
export async function ouvrirParCode(e: EnveloppeCode, code: string): Promise<Uint8Array> {
  return desenvelopper(e.cle, await cleDepuisCode(code, e.sel, e.iterations))
}

// -- Passkey (extension PRF) -----------------------------------------------------
// Une assertion WebAuthn locale suffit : pas besoin du serveur, donc utilisable
// hors ligne. L'assertion n'est pas vérifiée, seule compte la sortie PRF.

const versBase64Url = (b: ArrayBuffer) =>
  btoa(String.fromCharCode(...new Uint8Array(b)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
const depuisBase64Url = (s: string) =>
  Uint8Array.from(atob(s.replace(/-/g, '+').replace(/_/g, '/')), (c) => c.charCodeAt(0))

/** Faux si le navigateur annonce ne pas gérer PRF (vrai si inconnu : on essaie). */
export async function prfPeutEtreDisponible(): Promise<boolean> {
  if (typeof PublicKeyCredential === 'undefined') return false
  const capacites = await (
    PublicKeyCredential as unknown as { getClientCapabilities?: () => Promise<Record<string, boolean>> }
  ).getClientCapabilities?.()
  return capacites?.['extension:prf'] !== false
}

async function sortiePrf(sel: Uint8Array, credentialId?: string): Promise<{ sortie: ArrayBuffer; credentialId: string }> {
  const assertion = (await navigator.credentials.get({
    publicKey: {
      challenge: aleatoire(32),
      userVerification: 'required',
      ...(credentialId && { allowCredentials: [{ type: 'public-key', id: depuisBase64Url(credentialId) }] }),
      extensions: { prf: { eval: { first: sel as BufferSource } } },
    },
  })) as PublicKeyCredential | null
  const sortie = assertion?.getClientExtensionResults().prf?.results?.first
  if (!assertion || !sortie) throw new Error('Cette passkey ne permet pas de déverrouiller l’app sur cet appareil.')
  return { sortie: sortie as ArrayBuffer, credentialId: versBase64Url(assertion.rawId) }
}

export async function envelopperParPasskey(cleDonnees: Uint8Array): Promise<EnveloppePasskey> {
  const sel = aleatoire(32)
  const { sortie, credentialId } = await sortiePrf(sel)
  return { sel, credentialId, cle: await envelopper(cleDonnees, await cleDepuisPrf(sortie, sel)) }
}

export async function ouvrirParPasskey(e: EnveloppePasskey): Promise<Uint8Array> {
  const { sortie } = await sortiePrf(e.sel, e.credentialId)
  return desenvelopper(e.cle, await cleDepuisPrf(sortie, e.sel))
}
